import type { ThemeButtonData, ThemeButtonHover, ThemeColor, ThemeFont } from '@sites/site-theme/src/types';

import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { SpCustomButtonV2Component, type ThemeButtonEditorValue } from '@components/sp-custom-button-v2';

import { FONT_FAMILIES } from '@sites/site-theme/src/config/fonts.const';
import { ThemeFontTags } from '@sites/site-theme/src/enums/font-tags.enum';
import { ThemeStateService } from '@sites/site-theme/src/state';

/** The button editor as the theme's button panel shows it, with a link across to the font panel. */
@Component({
    selector: 'sp-themes-button-tab',
    templateUrl: './button-tab.component.html',
    styleUrls: ['./button-tab.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, SpCustomButtonV2Component],
})
export class SpThemesButtonTabComponent {
    private readonly themeState = inject(ThemeStateService);

    public readonly base = input.required<ThemeButtonData>();
    public readonly hover = input.required<ThemeButtonHover>();
    public readonly colorsPreset = input<ThemeColor[]>();

    public readonly valueChangeEvent = output<ThemeButtonEditorValue>();

    /**
     * The "edit" link beside the font hint, raised rather than acted on here.
     *
     * Switching the drawer to the font panel destroys the buttons panel around this tab, and that
     * panel restores its initial buttons when it is destroyed unsaved — so the switch has to be its
     * decision, not this one's, or the user's unsaved button edits vanish on the way across.
     */
    public readonly editFontsEvent = output<void>();

    public readonly FONT_FAMILIES = FONT_FAMILIES;

    /** The theme's `button` font, whose family the hint line names. */
    public readonly selectedThemeButtonFont = computed(() =>
        this.themeState.selectedFonts().find((font: ThemeFont) => font.tag === ThemeFontTags.button)
    );

    public openFontSettings(): void {
        this.editFontsEvent.emit();
    }
}
